import * as React from "react"
import { createPortal } from "react-dom"
import { type Session, type Speaker } from "../hooks/use-sessionize"
import {
  formatTimeDetailed,
  calculateSessionDuration,
} from "../utils/time-formatting"
import { formatPlainTextToHtml } from "../utils/text-formatting"
import { deduceSessionType, getSessionTags } from "../utils/session-type"
import SpeakerList from "./speaker-list"
import SessionTypeBadge from "./session-type-badge"
import Button from "./ui/button"
import CopyLinkButton from "./copy-link-button"
import { talkPath } from "../utils/talk-link"

const SessionModal: React.FC<{
  session: Session
  onClose: () => void
  onSpeakerClick: (speaker: Speaker) => void
}> = ({ session, onClose, onSpeakerClick }) => {
  const duration = calculateSessionDuration(session.startsAt, session.endsAt)
  const sessionType = deduceSessionType({
    startsAt: session.startsAt,
    endsAt: session.endsAt,
    room: session.room,
    isServiceSession: session.isServiceSession,
    speakers: session.speakers,
    categories: session.categories,
  })
  const tags = getSessionTags(session.categories)
  const formattedDescription = formatPlainTextToHtml(session.description)

  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose()
      }
    }

    const scrollbarWidth =
      window.innerWidth - document.documentElement.clientWidth
    document.body.style.overflow = "hidden"
    document.body.style.paddingRight = `${scrollbarWidth}px`
    document.addEventListener("keydown", handleKeyDown)

    return () => {
      document.body.style.overflow = "unset"
      document.body.style.paddingRight = "0px"
      document.removeEventListener("keydown", handleKeyDown)
    }
  }, [onClose])

  return createPortal(
    <>
      <div
        className="fixed inset-0 bg-white z-40"
        style={{ opacity: 0.5 }}
        onClick={onClose}
      ></div>
      <div
        className="fixed inset-0 z-50 flex justify-center items-center overflow-y-auto p-4"
        onClick={onClose}
      >
        <div
          className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[85vh] my-auto flex flex-col relative border border-gray-200"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="overflow-y-auto p-8 flex-grow">
            <SessionTypeBadge type={sessionType} className="mb-3" />
            <h2 className="text-3xl font-bold text-primary mb-4">
              {session.title || session.name}
            </h2>

            <div className="flex flex-wrap gap-x-6 gap-y-2 text-gray-600 mb-6">
              <span>
                <strong>Time:</strong> {formatTimeDetailed(session.startsAt)} -{" "}
                {formatTimeDetailed(session.endsAt)} ({duration} min)
              </span>
              <span>
                <strong>Room:</strong> {session.room}
              </span>
            </div>

            {tags.length > 0 && (
              <div className="mb-6 flex flex-wrap gap-2">
                {tags.map((tag) => (
                  <span
                    key={tag}
                    className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}

            {session.speakers && session.speakers.length > 0 && (
              <div className="mb-6">
                <h3 className="text-xl font-bold text-gray-800 mb-3">
                  Speakers
                </h3>
                <SpeakerList
                  speakers={session.speakers}
                  onSpeakerClick={onSpeakerClick}
                  variant="inline"
                  size="medium"
                />
              </div>
            )}

            {formattedDescription && (
              <div>
                <h3 className="text-xl font-bold text-gray-800 mb-3">
                  Description
                </h3>
                <div
                  className="text-gray-700 space-y-4 leading-relaxed [&_ul]:list-disc [&_ul]:pl-5 [&_li+li]:mt-1"
                  dangerouslySetInnerHTML={{ __html: formattedDescription }}
                />
              </div>
            )}
          </div>

          <div className="p-4 pb-8 text-center bg-white flex-shrink-0 relative flex justify-center gap-4">
            <div className="absolute bottom-full left-0 right-0 h-8 bg-gradient-to-t from-white to-transparent pointer-events-none"></div>
            <CopyLinkButton path={talkPath(session)} label="Copy talk link" />
            <Button onClick={onClose} variant="primary">
              Back to Schedule
            </Button>
          </div>
        </div>
      </div>
    </>,
    document.body
  )
}

export default SessionModal
